import { Button } from "@/components/flexkit/Button";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { WebhookListItem } from "@/components/webhooks/WebhookListItem";
import { useListWebhooks } from "@/queries/webhook-queries";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { formatError } from "@/utils/errors";
import type { FC } from "react";

export const WebhookList: FC = () => {
    const {
        data: webhooks,
        error: webhooksError,
        isPending: isPendingWebhooks,
        refetch: refetchWebhooks,
    } = useListWebhooks();

    const showErrorState = !isPendingWebhooks && webhooksError && (!webhooks || webhooks.length === 0);
    const showEmptyState = !isPendingWebhooks && !webhooksError && webhooks?.length === 0;

    if (showErrorState) {
        return (
            <EmptyStateCard
                title="Failed to load webhooks"
                description={webhooksError ? formatError(webhooksError) : "There was an error loading your webhooks."}
                data-testid="webhooks-error-state"
            >
                <Button
                    variant="secondary"
                    LeadingIcon={IconRefresh}
                    onClick={() => refetchWebhooks()}
                    data-tracking-id="retry-load-webhooks"
                >
                    Retry
                </Button>
            </EmptyStateCard>
        );
    }

    if (showEmptyState) {
        return (
            <EmptyStateCard
                title="No webhooks configured"
                description="Webhooks allow external services to trigger your automations. Create a webhook to get started."
                data-testid="webhooks-empty-state"
            />
        );
    }

    if (isPendingWebhooks) {
        return (
            <div className="flex flex-col gap-2" data-testid="webhooks-list">
                <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-55" />
                <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-35" />
                <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-15" />
            </div>
        );
    }

    return (
        <ul className="flex flex-col rounded-xl border border-border-base" data-testid="webhooks-list">
            {webhooks?.map((webhook) => (
                <WebhookListItem
                    key={webhook.id}
                    webhook={webhook}
                    className="border-b border-border-subtle first:rounded-t-lg last:rounded-b-lg last:border-b-0"
                />
            ))}
        </ul>
    );
};
